import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "@/components/intel/Layout";
import { ModuleShell, StatTile } from "@/components/intel/ModuleShell";
import { ScanAiChat } from "@/components/intel/ScanAiChat";
import { BreachExposureBanner } from "@/components/intel/BreachExposureBanner";
import { BreachRecordsList, extractBreachesFromRun, extractHibpStatus } from "@/components/intel/BreachRecordsList";
import { WatermarkOverlay } from "@/components/intel/WatermarkOverlay";
import { ChainOfCustodyPanel } from "@/components/intel/ChainOfCustodyPanel";
import { DocumentsPanel, type DocumentHit } from "@/components/intel/DocumentsPanel";
import { EvidenceTimeline } from "@/components/intel/EvidenceTimeline";
import { CaseWorkspace } from "@/components/intel/CaseWorkspace";
import { CaseDossierPanel } from "@/components/intel/CaseDossierPanel";
import { SourceReliabilityPanel } from "@/components/intel/SourceReliabilityPanel";
import { ContradictionPanel } from "@/components/intel/ContradictionPanel";
import { FootprintScorePanel } from "@/components/intel/FootprintScorePanel";
import { useAuth } from "@/lib/auth/AuthContext";
import {
  getScanRun,
  deleteScanRun,
  listScanNotes,
  addScanNote,
  deleteScanNote,
  flagFinding,
} from "@/lib/intel/feeds.functions";
import { ArrowLeft, Trash2, StickyNote, Send, Loader2, RefreshCw, Network, PanelsTopLeft } from "lucide-react";

export const Route = createFileRoute("/vault/$scanId/")({
  head: ({ params }) => ({
    meta: [
      { title: `Case ${params.scanId.slice(0, 8)} · CARIO` },
      { name: "description", content: "Saved case record with findings, entities, timeline and analyst notes." },
    ],
  }),
  component: VaultDetailPage,
  errorComponent: ({ error }) => (
    <AppLayout>
      <div className="p-6 space-y-3">
        <div className="text-sm text-danger">We couldn't open this case: {error.message}</div>
        <Link to="/vault" className="inline-flex rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs hover:bg-panel/70">Back to vault</Link>
      </div>
    </AppLayout>
  ),
  notFoundComponent: () => (
    <AppLayout>
      <div className="p-6 space-y-3">
        <div className="text-sm text-muted-foreground">That case isn't in your vault — it may have been deleted.</div>
        <Link to="/vault" className="inline-flex rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs hover:bg-panel/70">Back to vault</Link>
      </div>
    </AppLayout>
  ),
});

function VaultDetailPage() {
  const { scanId } = Route.useParams();
  return (
    <AppLayout>
      <VaultDetail scanId={scanId} />
    </AppLayout>
  );
}

function VaultDetail({ scanId }: { scanId: string }) {
  const get = useServerFn(getScanRun);
  const delRun = useServerFn(deleteScanRun);
  const listNotes = useServerFn(listScanNotes);
  const addNote = useServerFn(addScanNote);
  const delNote = useServerFn(deleteScanNote);
  const flagM = useServerFn(flagFinding);
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const { user } = useAuth();

  const isValidId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(scanId);

  const runQ = useQuery({
    queryKey: ["scan-vault", scanId],
    queryFn: () => get({ data: { id: scanId } }),
    enabled: isValidId,
    retry: 1,
  });
  const notesQ = useQuery({
    queryKey: ["scan-notes", scanId],
    queryFn: () => listNotes({ data: { scanRunId: scanId } }),
    enabled: isValidId,
    retry: 1,
  });

  const addM = useMutation({
    mutationFn: (body: string) => addNote({ data: { scanRunId: scanId, body } }),
    onSuccess: () => {
      setDraft("");
      qc.invalidateQueries({ queryKey: ["scan-notes", scanId] });
    },
  });
  const removeNoteM = useMutation({
    mutationFn: (id: string) => delNote({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scan-notes", scanId] }),
  });
  const removeRunM = useMutation({
    mutationFn: () => delRun({ data: { id: scanId } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scan-vault"] });
      window.location.href = "/vault";
    },
  });

  const run = runQ.data as any;
  const breaches = run ? extractBreachesFromRun(run) : [];
  const hibpStatus = run ? extractHibpStatus(run) : undefined;
  const documents: DocumentHit[] = (run?.raw?.documents as DocumentHit[] | undefined) ?? [];
  const requiredClearance = (run?.required_clearance ?? "L1") as string;
  const restricted = requiredClearance !== "L1";

  return (
    <ModuleShell
      eyebrow="Case Record"
      title={!isValidId ? "Case not found" : runQ.isLoading ? "Loading case…" : run?.target ?? "Case"}
      description={run ? `${run.target_type?.toUpperCase()} · ${run.status} · saved ${new Date(run.created_at).toLocaleString()}` : !isValidId ? "That link doesn't point to a case in your vault." : "Loading this case from your vault."}
      meta={
        <div className="flex items-center gap-2">
          <Link to="/vault" className="inline-flex items-center gap-2 rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs hover:bg-panel/70">
            <ArrowLeft className="h-3 w-3" /> Back to vault
          </Link>
          {run && (<>
          <Link to="/vault/$scanId/graph" params={{ scanId }} className="inline-flex items-center gap-2 rounded-md border border-cyan/40 bg-cyan/10 px-3 py-1.5 text-xs text-cyan hover:bg-cyan/20">
            <Network className="h-3 w-3" /> Graph
          </Link>
          <Link to="/vault/$scanId/canvas" params={{ scanId }} className="inline-flex items-center gap-2 rounded-md border border-cyan/40 bg-cyan/10 px-3 py-1.5 text-xs text-cyan hover:bg-cyan/20">
            <PanelsTopLeft className="h-3 w-3" /> Canvas
          </Link>
          <button
            onClick={() => runQ.refetch()}
            className="inline-flex items-center gap-2 rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs"
          >
            <RefreshCw className={`h-3 w-3 ${runQ.isFetching ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={() => { if (confirm("Permanently delete this scan and all its notes?")) removeRunM.mutate(); }}
            className="inline-flex items-center gap-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-1.5 text-xs text-danger hover:bg-danger/20"
          >
            <Trash2 className="h-3 w-3" /> Delete
          </button>
          </>)}
        </div>
      }
    >
      {!isValidId && (
        <div className="glass rounded-xl p-6 space-y-3">
          <div className="text-sm text-muted-foreground">That link doesn't point to a case we can open. It may be out of date.</div>
          <Link to="/vault" className="inline-flex rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs hover:bg-panel/70">Back to vault</Link>
        </div>
      )}
      {isValidId && runQ.isLoading && <div className="glass rounded-xl p-6 text-sm text-muted-foreground">Loading…</div>}
      {isValidId && !runQ.isLoading && runQ.error && (
        <div className="glass rounded-xl p-6 space-y-3">
          <div className="text-sm text-danger">We couldn't load this case: {(runQ.error as Error).message}</div>
          <div className="flex gap-2">
            <button onClick={() => runQ.refetch()} className="rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs hover:bg-panel/70">Try again</button>
            <Link to="/vault" className="rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs hover:bg-panel/70">Back to vault</Link>
          </div>
        </div>
      )}
      {isValidId && !runQ.isLoading && !runQ.error && !run && (
        <div className="glass rounded-xl p-6 space-y-3">
          <div className="text-sm text-muted-foreground">This case isn't in your vault. It may have been deleted, or it belongs to another account.</div>
          <Link to="/vault" className="inline-flex rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs hover:bg-panel/70">Back to vault</Link>
        </div>
      )}
      {run && (
        <>
          {restricted && (
            <WatermarkOverlay
              actor={user?.email ?? user?.id ?? "unknown"}
              scanId={scanId}
              clearance={`RESTRICTED · ${requiredClearance}`}
            />
          )}
          {breaches.length > 0 && <BreachExposureBanner breaches={breaches} />}
          <div className="grid gap-3 md:grid-cols-4">
            <StatTile
              label="Trust"
              value={run.risk_score === null || run.risk_score === undefined ? "—" : `${Math.max(0, Math.min(100, 100 - run.risk_score))}`}
              tone="cyan"
            />
            <StatTile
              label="Confidence"
              value={run.confidence === null || run.confidence === undefined ? "n/a" : `${run.confidence}%`}
              tone="success"
            />
            <StatTile label="Sources" value={run.source_count ?? 0} tone="cyan" />
            <StatTile label="Findings" value={(run.findings ?? []).length} tone="amber" />
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            <section className="glass rounded-xl p-4 lg:col-span-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Findings</div>
              <ul className="mt-3 space-y-2 text-sm">
                {(run.findings ?? []).map((f: any) => (
                  <li key={f.id} className="rounded border border-panel-border bg-background/40 p-2">
                    <div className="font-mono text-[10px] uppercase text-muted-foreground">[{f.severity}] {f.source}</div>
                    <div className="font-medium">{f.title}</div>
                    <div className="text-xs text-muted-foreground">{f.description}</div>
                    <div className="mt-1.5 flex justify-end">
                      <button
                        type="button"
                        onClick={async () => {
                          const reason = window.prompt("Why is this finding wrong? (optional — helps the AI avoid repeats)") ?? "";
                          try {
                            await flagM({ data: { scanRunId: scanId, target: run.target, source: f.source, findingTitle: f.title, reason: reason || undefined } });
                            alert("Flagged. Future scans will avoid this claim.");
                          } catch (err) {
                            alert(`Flag failed: ${err instanceof Error ? err.message : "unknown"}`);
                          }
                        }}
                        className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-danger"
                        title="Flag this finding as incorrect"
                      >
                        Flag as wrong
                      </button>
                    </div>
                  </li>
                ))}
                {!(run.findings ?? []).length && <li className="text-xs text-muted-foreground">No findings captured.</li>}
              </ul>

              {(run.entities ?? []).length > 0 && (
                <>
                  <div className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Entities</div>
                  <ul className="mt-2 flex flex-wrap gap-1.5 text-xs">
                    {(run.entities ?? []).map((e: any) => (
                      <li key={e.id} className="rounded border border-panel-border bg-background/40 px-2 py-1">
                        <span className="font-mono text-[10px] uppercase text-muted-foreground">{e.type}</span>{" "}
                        <span>{e.label ?? e.value}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {(run.timeline ?? []).length > 0 && (
                <>
                  <div className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">
                    Reconstructed timeline
                  </div>
                  <div className="mt-2">
                    <EvidenceTimeline events={run.timeline ?? []} />
                  </div>
                </>
              )}
            </section>

            <div className="flex flex-col gap-3">
              <ScanAiChat
                scanRunId={scanId}
                target={run.target}
                heightClass="h-[420px]"
              />
              <section className="glass rounded-xl p-4 flex flex-col">
              <div className="flex items-center gap-2">
                <StickyNote className="h-3.5 w-3.5 text-amber" />
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Case Notes</div>
              </div>
              <div className="mt-3 space-y-2 max-h-[280px] overflow-y-auto pr-1">
                {notesQ.isLoading && <div className="text-xs text-muted-foreground">Loading notes…</div>}
                {notesQ.data?.length === 0 && (
                  <div className="text-xs text-muted-foreground">No notes yet. Add the first one below.</div>
                )}
                {notesQ.data?.map((n) => (
                  <div key={n.id} className="rounded border border-panel-border bg-background/40 p-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-mono text-[10px] text-muted-foreground">{new Date(n.createdAt).toLocaleString()}</div>
                      <button
                        onClick={() => removeNoteM.mutate(n.id)}
                        className="text-muted-foreground hover:text-danger"
                        title="Delete note"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="mt-1 whitespace-pre-wrap text-sm">{n.body}</div>
                  </div>
                ))}
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const body = draft.trim();
                  if (body) addM.mutate(body);
                }}
                className="mt-3 border-t border-panel-border pt-3"
              >
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={3}
                  placeholder="Add an analyst note…"
                  className="w-full resize-none rounded-md border border-panel-border bg-background/40 p-2 text-sm outline-none focus:border-cyan/40"
                />
                {addM.error && (
                  <div className="mt-1 text-xs text-danger">{(addM.error as Error).message}</div>
                )}
                <div className="mt-2 flex justify-end">
                  <button
                    type="submit"
                    disabled={!draft.trim() || addM.isPending}
                    className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
                  >
                    {addM.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                    Save note
                  </button>
                </div>
              </form>
              </section>
            </div>
          </div>
          <BreachRecordsList breaches={breaches} status={hibpStatus} />
          <FootprintScorePanel
            collectors={(run?.raw?.collectors as any[]) ?? []}
            findings={(run?.findings as any[]) ?? []}
          />
          <SourceReliabilityPanel collectors={(run?.raw?.collectors as any[]) ?? []} />
          <ContradictionPanel scanRunId={scanId} collectors={(run?.raw?.collectors as any[]) ?? []} />
          <CaseDossierPanel collectors={(run?.raw?.collectors as any[]) ?? []} />
          <CaseWorkspace scanRunId={scanId} target={String(run?.target ?? "")} />
          <DocumentsPanel documents={documents} />
          <ChainOfCustodyPanel scanRunId={scanId} />
        </>
      )}
    </ModuleShell>
  );
}